// AKSU Business School - Luxury Executive Course eBook & Handbook Generator
// Domain: businessschool.aksu.edu.ng
// Generates luxury, brand-customized eBooks for all 15 Semester courses across DBA, MAF, and DAF
// Fully customized with Official University Crest, Business School Shield, and Dr. Ekere Jacob Daniel's 2025/2026 Course Outline

import { getCourseByCode, SEMESTER_COURSES } from './semesterCourseData';

export const getCourseEbookDossier = (courseCode) => {
  const course = getCourseByCode(courseCode) || SEMESTER_COURSES[0];
  const isEkereCourse = course.code === 'BUS 911' || course.code === 'DAF 911' || (course.code && course.code.includes('911'));

  const facilitator = course.lecturer || {
    name: isEkereCourse ? "Dr. Ekere Jacob Daniel" : "Assigned Faculty Chair",
    formalSalutation: isEkereCourse ? "Dr. Ekere Jacob Daniel, PhD" : "Doctoral Faculty Chair",
    rank: isEkereCourse ? "Associate Professor of Econometrics & Quantitative Analysis" : "Professor / Senior Lecturer",
    qualifications: isEkereCourse ? "B.Sc (Econs), M.Sc, PhD (Econometrics), FSS, MNAA" : "PhD, FCA, FNIM",
    title: isEkereCourse ? "Course Lecturer, BUS/DAF 911 & Director of Quantitative Modelling Lab" : "Course Facilitator & Doctoral Research Advisor",
    email: isEkereCourse ? "e.daniel@faculty.aksu.edu.ng" : "faculty@businessschool.aksu.edu.ng",
    avatar: isEkereCourse ? "/dr-daniel-ekere.png" : "/director-uwah.png"
  };

  // General references with verified ISBNs and external official hyperlinks
  const ekereReferences = [
    {
      id: 1,
      title: "Introduction to Econometrics",
      author: "Stock, J. H., & Watson, M. W.",
      year: "2021",
      edition: "4th Edition, Global Edition",
      publisher: "Pearson Education",
      isbn: "9781292264455",
      citation: "Stock, J. H., & Watson, M. W. (2021). Introduction to Econometrics (4th ed.). Pearson. ISBN: 9781292264455.",
      url: "https://www.pearson.com/en-gb/subject-catalog/p/introduction-to-econometrics-global-edition/P200000005728/9781292264455",
      altUrl: "https://www.worldcat.org/isbn/9781292264455",
      companionUrl: "https://www.princeton.edu/~mwatson/Stock-Watson-4_Global/",
      scope: "Core doctoral textbook for foundations of probability, statistical inference, single and multiple linear regression, instrumental variables, and time series econometrics."
    },
    {
      id: 2,
      title: "Introductory Econometrics: A Modern Approach",
      author: "Wooldridge, J. M.",
      year: "2025",
      edition: "8th Edition, International Edition",
      publisher: "Cengage Learning",
      isbn: "9798214050317",
      citation: "Wooldridge, J. M. (2025). Introductory Econometrics: A Modern Approach (8th ed.). Cengage. ISBN: 9798214050317.",
      url: "https://www.cengage.uk/9798214050317/",
      altUrl: "https://www.worldcat.org/isbn/9798214050317",
      scope: "Authoritative reference for applied econometrics, heteroskedasticity, serial correlation, dynamic panel data estimation, and computer exercises adaptable to Stata, R, and Python."
    },
    {
      id: 3,
      title: "Econometric Analysis",
      author: "Greene, W. H.",
      year: "2018",
      edition: "8th Edition",
      publisher: "Pearson",
      isbn: "9780134461366",
      citation: "Greene, W. H. (2018). Econometric Analysis (8th ed.). Pearson. ISBN: 9780134461366.",
      url: "https://www.pearson.com/en-us/subject-catalog/p/econometric-analysis/P200000005726/9780134461366",
      altUrl: "http://people.stern.nyu.edu/wgreene/Text/econometricanalysis.htm",
      scope: "Doctoral standard treatise for classical linear regression theory, generalized least squares, instrumental variables, maximum likelihood estimation, and non-linear microeconometric models."
    },
    {
      id: 4,
      title: "Mostly Harmless Econometrics: An Empiricist's Companion",
      author: "Angrist, J. D., & Pischke, J.-S.",
      year: "2009",
      edition: "1st Edition",
      publisher: "Princeton University Press",
      isbn: "9780691120355",
      citation: "Angrist, J. D., & Pischke, J.-S. (2009). Mostly Harmless Econometrics. Princeton University Press. ISBN: 9780691120355.",
      url: "https://press.princeton.edu/books/paperback/9780691120355/mostly-harmless-econometrics",
      altUrl: "https://www.mostlyharmlesseconometrics.com/",
      scope: "Empirical strategies in causal inference, natural experiments, instrumental variables (2SLS), panel fixed effects, and differences-in-differences."
    },
    {
      id: 5,
      title: "Using Stata for Principles of Econometrics",
      author: "Adkins, L. C., & Hill, R. C.",
      year: "2011",
      edition: "4th Edition",
      publisher: "John Wiley & Sons",
      isbn: "9781118032084",
      citation: "Adkins, L. C., & Hill, R. C. (2011). Using Stata for Principles of Econometrics (4th ed.). Wiley. ISBN: 9781118032084.",
      url: "https://www.wiley.com/en-us/Using+Stata+for+Principles+of+Econometrics%2C+4th+Edition-p-9781118032084",
      altUrl: "http://www.principlesofeconometrics.com/stata.htm",
      scope: "Software companion providing hands-on Stata code, do-files, diagnostic routines, and empirical replication pipelines for corporate and business datasets."
    }
  ];

  const ekereSoftwareTools = [
    {
      name: "Stata 18 MP",
      category: "Econometric Package",
      url: "https://www.stata.com/",
      desc: "University-licensed suite for regression, 2SLS, dynamic panel data, GMM, and diagnostic testing."
    },
    {
      name: "The R Project for Statistical Computing",
      category: "Open Source Computing",
      url: "https://www.r-project.org/",
      desc: "Reproducible econometrics, tidyverse wrangling, and packages: plm, fixest, sandwich, lmtest."
    },
    {
      name: "Python Statsmodels",
      category: "Scientific Python",
      url: "https://www.statsmodels.org/",
      desc: "Statistical modeling, linear models, time series analysis (ARIMA, VAR), and hypothesis testing."
    },
    {
      name: "Python Linearmodels",
      category: "Panel Econometrics",
      url: "https://bashtage.github.io/linearmodels/",
      desc: "Advanced panel data models (Fixed Effects, First Difference, Between), Instrumental Variables, and GMM."
    }
  ];

  // Tailored chapters for BUS/DAF 911
  const ekereChapters = [
    {
      number: "Chapter 1",
      title: "Foundations of Statistical Reasoning & The Classical Linear Regression Model",
      subtitle: "Probability Distributions, Sampling Inference & OLS Estimation (Weeks 1 – 3)",
      content: [
        "In doctoral business inquiries, rigorous statistical reasoning forms the cornerstone of valid empirical claims. As executives and scholars examine corporate performance, market disruptions, and public policy outcomes, observed data must be connected to underlying population parameters through defensible probabilistic foundations.",
        "This chapter examines probability distributions, random variables, sampling distributions, point estimation, and confidence intervals. We deconstruct the Gauss-Markov theorem underpinning Ordinary Least Squares (OLS) estimation, proving the conditions under which the OLS estimator is Best Linear Unbiased Estimator (BLUE).",
        "We extend the framework to hypothesis testing: Type I and Type II errors, statistical power, single and joint linear restriction tests (t-tests and F-tests), confidence intervals, prediction bands, and functional form selection (log-linear, log-log, and polynomial models) essential for business research."
      ],
      keyTakeaways: [
        "Mathematical derivation and intuition of the Gauss-Markov theorem (BLUE properties).",
        "Understanding sampling distributions, Type I/Type II trade-offs, and statistical power in empirical research.",
        "Formulating joint F-tests and nested model comparisons for strategic management questions."
      ]
    },
    {
      number: "Chapter 2",
      title: "Violations of Classical Assumptions & Robust Econometric Inference",
      subtitle: "Heteroskedasticity, Autocorrelation & Robust Covariance Estimation (Week 4)",
      content: [
        "In cross-sectional and time-series business data, the classical assumptions of homoskedasticity and non-autocorrelation are almost universally violated. In firm-level datasets where firm sizes vary across orders of magnitude (e.g. quoted companies on the Nigerian Exchange), error variances naturally scale with asset base.",
        "When error variances are heteroskedastic, OLS estimators remain unbiased and consistent, but standard errors are biased, rendering classical t-statistics and F-statistics invalid. Candidates master diagnostic tests: Breusch-Pagan, Koenker, and White tests for heteroskedasticity.",
        "We master remedial measures: White-Huber heteroskedasticity-consistent standard errors, Feasible Generalized Least Squares (FGLS), and Newey-West Heteroskedasticity and Autocorrelation Consistent (HAC) standard errors for time series and dynamic financial models."
      ],
      keyTakeaways: [
        "Distinguishing between consequences of heteroskedasticity on coefficient consistency versus standard error validity.",
        "Executing Breusch-Pagan and White tests in Stata and Python.",
        "Reporting robust Huber-White and Newey-West standard errors in doctoral dissertation tables."
      ]
    },
    {
      number: "Chapter 3",
      title: "Endogeneity, Instrumental Variables (2SLS) & Panel Data Models",
      subtitle: "Resolving Omitted Variable Bias, Simultaneity & Unobserved Heterogeneity (Weeks 5 – 8)",
      content: [
        "The central challenge in doctoral business research is distinguishing correlation from causation. Endogeneity—arising from omitted variables, measurement error, or simultaneity—renders OLS estimates biased and inconsistent, invalidating policy recommendations.",
        "This chapter details the Instrumental Variables (IV) approach and Two-Stage Least Squares (2SLS). Candidates evaluate instrument validity: the relevance condition (strong first-stage correlation with Cov(Z,X) ≠ 0) and the exogeneity/orthogonality condition (Cov(Z,u) = 0). We detail diagnostic tests: the Hausman endogeneity test, Stock-Yogo weak instrument tests, and the Sargan-Hansen J overidentification test.",
        "We then transition to panel data econometrics, leveraging repeated observations on firms or countries over time. Candidates deconstruct Pooled OLS, Fixed Effects (within-transformation), First-Differencing, and Random Effects (GLS). We apply the Hausman specification test to decide between Fixed and Random effects, and implement firm-level clustered standard errors to account for within-unit serial correlation.",
        "Finally, we address qualitative response models for binary choice outcomes (Logit, Probit, Maximum Likelihood Estimation) and diagnostic specification tests (Ramsey RESET, Chow structural break tests, and outlier influence diagnostics)."
      ],
      keyTakeaways: [
        "Formulating defensible identification strategies and natural experiments for executive research.",
        "Executing Two-Stage Least Squares (2SLS) and interpreting first-stage F-statistics.",
        "Mastering the Fixed Effects vs Random Effects choice and controlling for unobserved firm heterogeneity."
      ]
    },
    {
      number: "Chapter 4",
      title: "Time Series Econometrics, Forecasting & Hands-On Software Laboratories",
      subtitle: "Unit Roots, ARIMA, VAR & Practical Estimation in Stata, R & Python (Weeks 9 – 11)",
      content: [
        "Macroeconomic and capital market variables exhibit strong persistence, trends, and non-stationarity. Regressing one non-stationary series on another produces spurious regression with deceptively high R-squared values and inflated t-statistics.",
        "Candidates master stationarity testing: Augmented Dickey-Fuller (ADF) and Phillips-Perron (PP) tests, Box-Jenkins ARIMA identification and forecasting, and Vector Autoregressions (VAR) for multivariate time series dynamics and impulse response functions.",
        "The chapter provides hands-on code laboratories across three computing environments: Stata (commands: regress, ivregress 2sls, xtreg, fe, estat hettest, outreg2), Python (statsmodels and linearmodels), and R (plm and fixest). Candidates master reproducible research workflows, script documentation, and publication-ready regression output formatting."
      ],
      keyTakeaways: [
        "Identifying non-stationarity, stochastic trends, and executing unit root tests.",
        "Hands-on execution of econometric models in Stata, R, and Python with script reproducibility.",
        "Formatting publication-grade regression tables conforming to APA 7th edition and premier journal guidelines."
      ]
    },
    {
      number: "Chapter 5",
      title: "Empirical Applications to Published Firm-Level Datasets & Doctoral Research Synthesis",
      subtitle: "Compustat, Survey Data, Replication, Robustness & Viva Voce Preparation (Weeks 12 – 13)",
      content: [
        "The capstone chapter applies all econometric methodologies to authentic firm-level datasets (Compustat-style accounting data, Nigerian Exchange corporate filings, and World Bank enterprise surveys).",
        "Candidates learn the craft of empirical business research: data cleaning, winsorization of extreme outliers, handling missing observations, survival bias corrections, and constructing multi-model robustness matrices.",
        "Scholars synthesize their empirical findings into an executive doctoral research project presentation, defending their econometric model specifications, identification assumptions, and policy recommendations before a mock doctoral viva voce committee."
      ],
      keyTakeaways: [
        "Handling complex micro datasets, panel balance issues, and data cleaning protocols.",
        "Constructing comprehensive robustness check tables (alternative estimators, subsamples, placebo tests).",
        "Defending econometric methodology with intellectual rigor during doctoral oral examinations."
      ]
    }
  ];

  return {
    courseCode: course.code,
    crossListing: course.crossListing || (isEkereCourse ? "BUS/DAF 911" : course.code),
    courseTitle: isEkereCourse ? "Advanced Quantitative Analysis and Econometrics" : course.title,
    programmeName: course.programmeName,
    degreeLevel: course.degreeLevel,
    credits: course.credits || 3,
    semester: isEkereCourse ? "First Semester 2025/2026" : (course.semester || "First Semester 2026/2027"),
    academicSession: isEkereCourse ? "2025/2026" : "2025/2026",
    department: course.department || "Department of Business Administration",
    schedule: course.schedule || "Saturdays 12:30 - 15:30",
    venue: course.venue || "Doctoral Econometric Lab & Virtual Studio, Obio Akpa Campus / Uyo Liaison Office",
    
    // Official Branding Logos
    officialLogos: {
      universityCrest: "/aksu-university-crest-official.jpg",
      businessSchoolShield: "/aksu-business-school-shield-official.png",
      universityCrestAlt: "/Logo.png"
    },

    facilitator: {
      name: facilitator.name,
      salutation: facilitator.formalSalutation || facilitator.name,
      rank: facilitator.rank,
      qualifications: facilitator.qualifications,
      title: facilitator.title,
      email: facilitator.email,
      phone: facilitator.phone || "+234 802 334 1172",
      office: facilitator.office || "Quantitative Economics Suite 2A, Main Campus, Ikot Akpaden",
      bio: facilitator.bio || "Distinguished scholar and executive advisor in econometrics and quantitative analysis.",
      avatar: facilitator.avatar || (isEkereCourse ? "/dr-daniel-ekere.png" : "/director-uwah.png"),
      researchInterests: facilitator.researchInterests || ["Dynamic Panel Econometrics", "Structural Equation Modelling"]
    },

    overview: course.overview,
    learningOutcomes: course.learningOutcomes || [
      "Master foundational and cutting-edge theoretical frameworks governing executive quantitative decision-making.",
      "Formulate robust empirical econometric methodologies bridging corporate operational data with strategic policy.",
      "Diagnose and remediate violations of classical regression assumptions: heteroskedasticity, autocorrelation, and endogeneity.",
      "Execute Instrumental Variables (2SLS) and dynamic panel models on firm-level datasets using Stata, R, and Python.",
      "Produce publication-grade doctoral research outputs aligned with international ABS and Scopus benchmarks."
    ],

    modules: course.modules || [],
    materials: course.materials || [],
    assignments: course.assignments || [],
    generalReferences: isEkereCourse ? ekereReferences : [],
    softwareTools: isEkereCourse ? ekereSoftwareTools : [],
    
    // Institutional Foreword
    foreword: `This official Executive Course Handbook and Comprehensive Study Reader is issued under the authority of the Directorate of Akwa Ibom State University Business School and the University Senate for the 2025/2026 Academic Session. Formulated specifically for the Doctor of Business Administration (DBA) and Doctor of Accounting and Finance (DAF) cohorts, this volume synthesises foundational statistical theory, rigorous econometric modeling, and hands-on computing workflows in Stata, R, and Python to equip executive scholars with world-class methodological mastery.`,
    
    chapters: isEkereCourse ? ekereChapters : [
      {
        number: "Chapter 1",
        title: `Foundations and Paradigms of ${course.title}`,
        subtitle: "Theoretical Grounding, Axiomatic Assumptions & Literature Synthesis",
        content: [
          `In executive postgraduate education, the study of ${course.title} requires moving beyond tactical operational management to grapple with fundamental structural paradigms. Historically, organizations in sub-Saharan Africa have operated within dual institutional contexts—balancing formal regulatory mandates with informal market dynamics. Understanding these forces demands deep theoretical grounding.`,
          `Under the academic leadership of ${facilitator.formalSalutation || facilitator.name}, this course bridges theoretical rigour with actionable strategic interventions. The core analytical framework posits that sustainable competitive advantage emerges from the deliberate alignment of internal resources, dynamic capabilities, and macroeconomic foresight.`
        ],
        keyTakeaways: [
          "Critical evaluation of classical vs. modern paradigms governing this discipline.",
          "Identification of structural friction points in emerging market implementations.",
          "Methodological alignment of research questions with empirical observation."
        ]
      }
    ],

    recommendedReadings: isEkereCourse ? ekereReferences.map(r => ({ citation: r.citation, scope: r.scope, url: r.url, isbn: r.isbn })) : [
      {
        citation: "Teece, D. J. (2020). Dynamic Capabilities and Strategic Management: Organizing for Innovation and Growth. Oxford University Press.",
        scope: "Foundational theory of dynamic capabilities and sensing/seizing mechanisms."
      },
      {
        citation: "Saunders, M., Lewis, P., & Thornhill, A. (2023). Research Methods for Business Students (9th Edition). Pearson Education.",
        scope: "Doctoral research design, ontological alignment, and methodological coherence."
      }
    ],

    assessmentStructure: [
      { component: "Continuous Assessment 1 (CA1): Empirical Econometric Diagnostic Paper (Stata/R/Python)", weight: "20%" },
      { component: "Continuous Assessment 2 (CA2): Instrumental Variables (2SLS) & Dynamic Panel Model on Firm-Level Data", weight: "20%" },
      { component: "Classroom Seminar Colloquium, Laboratory Exercises & Peer Review Defence", weight: "10%" },
      { component: "Comprehensive Capstone Examination & Doctoral Research Presentation (Viva Voce)", weight: "50%" }
    ]
  };
};
